import { SelfContainedCommand, CommandExecutionContext } from "../../types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "../../types/standardToolResults";
import { createFileLogger } from "../../utils/logger";
import useEntityStore from "../../stores/entityStore";
import useSpaceStore from "../../stores/spaceStore";
import { BaseEntityType } from "../../types";
// ✅ PHASE 2: HierarchyStore removed - filtering moved to EntityStore
import { useDataspaceTreeStore } from "../../stores/dataspaceTreeStore";
import { resolveEntityId } from "./entityIdResolver";

const log = createFileLogger('FOLDER_LIST');

export const folderListCommand: SelfContainedCommand = {
  id: 'folder_list',
  description: `List all entities in a folder or at root level.

**SUPPORTED ENTITIES:**
All entity types

Returns entities with id, name, entityType, childCount (for folders). Use folderId=null or omit to list root-level entities.

**EXAMPLE:**

{ "folderId": "group_guitar_ocean_zodiac" }`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder ID (group_*) to list contents of. If not provided or null, lists root-level entities.',
        required: false,
        defaultValue: null,
        examples: [
          'group_guitar_ocean_zodiac',
          'group_meadow_silver_compass',
          'group_cascade_thunder_phoenix'
        ],
        validator: (value: any) => {
          // null/undefined is valid (means list root level)
          if (value === null || value === undefined) return null;
          
          // Must be a string
          if (typeof value !== 'string') return 'Folder ID must be a string';
          
          // Cannot be empty string
          if (value.trim().length === 0) return 'Folder ID cannot be empty string - use null to list root entities';
          
          // Note: Existence validation happens in handler (can't check entityStore here)
          return null;
        }
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { folderId: 'group_red_apple_blue_banana' },
        expectedOutput: createSuccessResult('Folder contents retrieved', {
          folderId: 'group_red_apple_blue_banana',
          location: 'folder',
          count: 3,
          entities: [
            { id: 'doc_green_tree_yellow_sun', name: 'Report.docx', entityType: 'Doc' },
            { id: 'doc_purple_ocean_bright_moon', name: 'Notes.docx', entityType: 'Doc' },
            { id: 'group_dark_forest_calm_river', name: 'Subfolder', entityType: 'Group', childCount: 5 }
          ]
        }),
        description: 'List contents of a specific folder by ID',
        context: 'When exploring what files/subfolders are inside a particular folder'
      },
      {
        input: {},
        expectedOutput: createSuccessResult('Root entities retrieved', {
          folderId: null,
          location: 'root',
          count: 2,
          entities: [
            { id: 'doc_calm_river_bright_star', name: 'Document.docx', entityType: 'Doc' },
            { id: 'group_happy_garden_bright_dawn', name: 'My Folder', entityType: 'Group', childCount: 10 }
          ]
        }),
        description: 'List all root-level entities (no folderId provided)',
        context: 'When exploring the top-level workspace to see all items not in folders'
      },
      {
        input: { folderId: null },
        expectedOutput: createSuccessResult('Root entities retrieved', {
          folderId: null,
          location: 'root',
          count: 2,
          entities: [
            { id: 'doc_calm_river_bright_star', name: 'Document.docx', entityType: 'Doc' },
            { id: 'group_happy_garden_bright_dawn', name: 'My Folder', entityType: 'Group', childCount: 10 }
          ]
        }),
        description: 'Explicitly list root-level entities (folderId = null)',
        context: 'When you explicitly want root-level items'
      }
    ],
    bad: [
      {
        input: { folderId: '' },
        error: 'Folder ID cannot be empty string - use null to list root entities',
        why: 'Empty string is ambiguous and invalid',
        fix: 'Use null or omit the parameter to list root entities, or provide a valid folder ID'
      },
      {
        input: { folderId: 'nonexistent_folder_id' },
        error: 'Folder not found',
        why: 'The folder ID does not exist in the workspace',
        fix: 'Use entity_find or entity_get to discover valid folder IDs first, or use folder_list without folderId to see root folders'
      }
    ]
  },
  
  performance: {
    expectedDuration: 200,
    complexity: 'low'
  },
  
  formatViewData: (result: any, input: any) => {
    if (!result || result.status !== 'success') {
      return undefined;
    }
    
    const data = result.data || {};
    const entities = data.entities || [];
    const count = data.count || 0;
    const location = data.location || 'unknown';
    
    // ✅ CRITICAL: Renderer expects count, location, entities at TOP LEVEL!
    // FolderListRenderer.tsx accesses view.count, view.entities, view.location directly
    const formattedEntities = entities.map((e: any) => ({
      id: e.id,
      name: e.name,
      entityType: e.entityType,
      ...(e.childCount !== undefined ? { childCount: e.childCount } : {})
    }));
    
    return {
      type: 'folder_list',
      summary: result.message || `Found ${count} items`,
      // Top-level fields for renderer
      count,
      location,
      entities: formattedEntities,
      // Also keep in data object for consistency with ToolViewData interface
      data: {
        count,
        location,
        entities: formattedEntities
      }
    };
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { folderId } = params;
    
    try {
      const entityStore = useEntityStore.getState();
      let entities;
      
      if (folderId) {
        // ✅ CRITICAL: Resolve llmId to _id if needed (supports both formats)
        const resolution = resolveEntityId(folderId);
        const resolvedFolderId = resolution.resolved;
        
        log.info('[folder_list] Resolved folder ID:', { 
          original: folderId, 
          resolved: resolvedFolderId,
          isLlmId: resolution.isLlmId 
        });
        
        const folder = entityStore.entities.find((e: BaseEntityType) => e._id === resolvedFolderId);
        
        // ✅ SECURITY: Check if folder exists AND is not trashed/hidden
        if (!folder || (folder as any).trashed === true || (folder as any).hidden === true) {
          return createErrorResult(
            'Folder not found',
            `Folder with ID ${folderId} does not exist or has been deleted`,
            'validation',
            { data: { folderId, resolvedId: resolvedFolderId } }
          );
        }
        
        // ✅ CRITICAL: Use tree system for consistency with UI!
        // Get children from dataspace_edges (single source of truth)
        // Already filters out trashed and hidden entities via EntityStore
        const { getChildNodeIds } = useDataspaceTreeStore.getState();
        const displayableEntities = entityStore.getDisplayableEntities();
        
        const childNodeIds = getChildNodeIds(resolvedFolderId);
        const allChildren = displayableEntities.filter(e => childNodeIds.includes(e._id));
        
        // Filter out system entity types (trashed and hidden already filtered by displayableEntities)
        entities = allChildren.filter(e => 
          e.entityType !== 'DocVersion' &&
          e.entityType !== 'Message'
        );
        
        log.info('[folder_list] Listed folder contents:', { 
          folderId, 
          count: entities.length,
          totalChildren: allChildren.length,
          filteredOut: allChildren.length - entities.length
        });
      } else {
        // List root-level entities (no folderId provided)
        // ✅ CRITICAL: Use tree system for consistency with UI!
        // Get root entities from space
        const { getChildNodeIds } = useDataspaceTreeStore.getState();
        const entityStore = useEntityStore.getState();
        const displayableEntities = entityStore.getDisplayableEntities();
        const spaceId = useSpaceStore.getState().activeSpace?.id;
        
        const rootNodeIds = getChildNodeIds(spaceId || '');
        const rootEntities = displayableEntities.filter(e => rootNodeIds.includes(e._id));
        
        // Filter out system entity types (trashed and hidden already filtered by displayableEntities)
        entities = rootEntities.filter(e =>
          e.entityType !== 'DocVersion' &&
          e.entityType !== 'Message'
        );
        
        log.info('[folder_list] Listed root entities:', { 
          count: entities.length,
          totalRoot: rootEntities.length,
          filteredOut: rootEntities.length - entities.length
        });
      }
      
      const result = {
        folderId: folderId || null,
        location: folderId ? 'folder' : 'root',
        count: entities.length,
        entities: entities.map(e => ({
          id: e._id,
          name: e.name,
          entityType: e.entityType,
          icon: e.icon,
          created_at: e.created_at,
          ...(e.entityType === 'Group' ? {
            childCount: e.skeleton?.childEntities?.length || 0
          } : {})
        }))
      };
      
      return createSuccessResult(
        `Found ${entities.length} ${folderId ? 'entities in folder' : 'root-level entities'}`,
        result,
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'entityStore'
          } as any
        }
      );
      
    } catch (error: any) {
      log.error('[folder_list] Error:', error);
      return createErrorResult(
        'Failed to list folder contents',
        error.message || 'Unknown error',
        'execution',
        {
          data: { folderId },
          metadata: { executionTime: Date.now() - startTime }
        }
      );
    }
  }
};

